import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Stack,
  SvgIcon,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';

import {
  AppPrimaryButton,
  // AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';

import { GuardrailsService } from 'services/GuardrailsService';

import CreateCustomGuardrailModal from '../customPackage/CreateCustomGuardrailModal';

import { Toast } from 'shared/helpers';
import useSize from 'shared/components/Hooks';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface GuardrailPolicy {
  _id: string;
  policyName: string;
  description: string;
  responseActions: string;
  enabled: boolean;
  type: string;
  monitoring: string;
  guardrailsGroupId: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  organizationId: string | null;
  isActive: boolean;
  __v: number;
}

interface PolicyGroup {
  _id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  organizationId: string | null;
  isActive: boolean;
  __v: number;
  policies: GuardrailPolicy[];
}

const PolicyList: React.FC = () => {
  const [groups, setGroups] = useState<PolicyGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const { isMobile } = useSize();
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );
  const [openCustomGuardrailModal, setOpenCustomGuardrailModal] =
    useState(false);

  const [editingPolicy, setEditingPolicy] = useState<GuardrailPolicy | null>(
    null
  );

  // LOAD POLICIES
  const loadGroupWithPolicies = (): void => {
    setLoading(true);

    GuardrailsService.getAllGroupWithPolicies()
      .then((response) => {
        setGroups(response?.data?.data?.groups || []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // TOGGLE
  const handleToggle = (
    groupId: string,
    policyId: string,
    checked: boolean,
    item: any
  ): void => {
    setGroups((prev) =>
      prev.map((group) => {
        if (group._id !== groupId) {
          return group;
        }

        return {
          ...group,
          policies: group.policies.map((item) =>
            item._id === policyId
              ? {
                  ...item,
                  enabled: checked,
                }
              : item
          ),
        };
      })
    );

    const finalPayload = {
      ...item,
      enabled: checked,
    };

    GuardrailsService.updatePolicy(policyId, finalPayload)
      .then(() => {})
      .catch((error) => {
        console.error(error);
        Toast.showErrorMessage('Failed to update policy');
      });
  };

  // CREATE POLICY
  const createPolicy = (payload: any): void => {
    const { ...finalPayload } = payload;

    const apiCall = editingPolicy
      ? GuardrailsService.updatePolicy(editingPolicy._id, finalPayload)
      : GuardrailsService.createPolicy(finalPayload);

    apiCall
      .then(() => {
        Toast.showSuccessMessage(
          editingPolicy
            ? 'Policy updated successfully'
            : 'Policy created successfully'
        );

        setOpenCustomGuardrailModal(false);
        setEditingPolicy(null);

        loadGroupWithPolicies();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // HANDLE CREATE/UPDATE CUSTOM GUARDRAIL
  const handleCreateCustomGuardrail = (policyData: any): void => {
    // NEW GROUP FLOW
    if (policyData.guardrailsGroupId === 'new_group') {
      GuardrailsService.createGroup({
        name: policyData.newGroupName,
      })
        .then((res) => {
          createPolicy({
            ...policyData,
            guardrailsGroupId: res?.data?.data?._id,
          });
        })
        .catch((error) => {
          console.log(error);
        });

      return;
    }

    // NORMAL FLOW
    createPolicy(policyData);
  };

  // EDIT
  const handleEditPolicy = (policy: GuardrailPolicy): void => {
    setEditingPolicy(policy);
    setOpenCustomGuardrailModal(true);
  };

  // DELETE
  const handleDeleteClick = (policyId: string): void => {
    setLoading(true);

    GuardrailsService.deletePolicy(policyId)
      .then((response) => {
        if (response.data.data.id) {
          Toast.showSuccessMessage('Policy deleted successfully');
          loadGroupWithPolicies();
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // TOTAL COUNT
  const totalGuardrails = useMemo(
    () =>
      groups?.reduce((acc, group) => acc + (group.policies?.length || 0), 0),
    [groups]
  );

  useEffect(() => {
    loadGroupWithPolicies();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="policy-list-container">
      {/* HEADER */}
      <Box className="policy-header">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          flexWrap="wrap"
          gap={2}
        >
          <Typography className="page-title">
            {totalGuardrails} guardrails defined
          </Typography>

          <Stack direction="row" className="mb-5" spacing={2}>
            {/* <AppPrimaryButtonOutlined
              title="Sensitivity Labels"
              className="btn-small"
              startIcon={<LocalOfferOutlinedIcon />}
              onClick={() => {}}
            /> */}

            {orgACLConfig.policies?.includes(
              'policies.guardrails.custom.create'
            ) && (
              <AppPrimaryButton
                title={`Add ${isMobile ? '' : 'Custom Guardrail'}`}
                className={isMobile ? 'btn-extra-small' : 'btn-small'}
                onClick={() => {
                  setEditingPolicy(null);
                  setOpenCustomGuardrailModal(true);
                }}
                startIcon={
                  isMobile ? (
                    <></>
                  ) : (
                    <SvgIcon
                      component={Plus}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  )
                }
              />
            )}
          </Stack>
        </Stack>
      </Box>

      {/* LIST */}
      <Box className="accordion-wrapper">
        {groups.map((group) => {
          const enabledCount = group.policies.filter(
            (item) => item.enabled
          ).length;

          const totalCount = group.policies.length;

          return (
            <Accordion
              key={group._id}
              disableGutters
              elevation={0}
              className="policy-accordion"
              defaultExpanded
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className="policy-summary"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography className="group-title">
                      {group.name}
                    </Typography>

                    <Typography className="group-count">
                      {enabledCount}/{totalCount} enabled
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails className="policy-details">
                {group.policies.map((item) => (
                  <Box className="policy-card" key={item._id}>
                    <Box className="policy-card-left">
                      <Switch
                        checked={item.enabled}
                        onChange={(event) => {
                          if (
                            orgACLConfig.policies?.includes(
                              'policies.guardrails.custom.onOff'
                            )
                          ) {
                            handleToggle(
                              group._id,
                              item._id,
                              event.target.checked,
                              item
                            );
                          }
                        }}
                      />
                    </Box>

                    <Box className="policy-card-content">
                      <Box className="policy-title-row">
                        <Typography className="policy-title">
                          {item.policyName}
                        </Typography>

                        {item.type === 'custom' && (
                          <Box className="policy-title-actions">
                            <Chip
                              label="Custom"
                              size="small"
                              className="custom-chip"
                            />
                            {orgACLConfig.policies?.includes(
                              'policies.guardrails.custom.edit'
                            ) && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditPolicy(item)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {orgACLConfig.policies?.includes(
                              'policies.guardrails.custom.delete'
                            ) && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(item._id)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>

                      <Typography className="policy-description">
                        {item.description}
                      </Typography>

                      <Typography className="policy-sub-description">
                        Response Actions: {item.responseActions}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* MODAL */}
      <CreateCustomGuardrailModal
        open={openCustomGuardrailModal}
        onClose={() => {
          setOpenCustomGuardrailModal(false);
          setEditingPolicy(null);
        }}
        onSubmit={handleCreateCustomGuardrail}
        editData={editingPolicy}
      />
    </Box>
  );
};

export default PolicyList;
